import React from 'react';
import { analytics } from 'core/analytics';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';

export class ErrorState {
  @observable error: Error | null = null;

  @observable errorCleared = false;

  @observable errorClearedAttempts = 0;

  reactComponent: ErrorHandlerInner = null;

  constructor(reactComponent: ErrorHandlerInner) {
    this.reactComponent = reactComponent;
  }

  @action SetError = (error: Error) => {
    this.error = error;
  };

  @action ClearError = () => {
    this.error = null;
    this.errorClearedAttempts += 1;
    this.errorCleared = true;
    this.reactComponent.clearReactErrorState();
    // After 60 seconds set errorCleared to false.
    // The strategy here is that if either:
    //    a) The error is in an infinite loop
    //    b) The error is retriggred by the user in a short period of time
    // We don't want to keep trying to reload the component and our best strategy is for the user to refresh.
    // If, on the other hand, the error is not being triggered repeatedly,
    // it might be fine to allow the user to just try to recover again.
    setTimeout(() => {
      this.errorCleared = false;
    }, 60000);
  };

  @computed get key() {
    /* Each time we ClearError change the key to attmpt to reload the component */
    return this.errorClearedAttempts;
  }
}

export const ErrorRecoveryMessage = observer(
  ({
    typeName,
    tryRecover,
    errorState,
  }: {
    typeName: string;
    tryRecover?: () => void;
    errorState: ErrorState;
  }) => {
    if (!tryRecover) {
      // eslint-disable-next-line no-param-reassign
      tryRecover = () => errorState.ClearError();
    }
    let message = (
      <>
        Oops! There was a problem with {typeName}.&nbsp;
        <a
          href={window.location.href}
          onClick={e => {
            tryRecover();
            e.preventDefault();
          }}
        >
          Try again
        </a>
        .
      </>
    );
    if (errorState.errorCleared) {
      message = (
        <>
          Sorry! We couldn&apos;t recover from an error in {typeName}.
          Please&nbsp;
          <a href={window.location.href} onClick={window.location.reload}>
            refresh
          </a>{' '}
          &nbsp; to try again.
        </>
      );
    }
    return <>{message}</>;
  },
);

interface ErrorHandlerProps {
  ErrorComponent: React.ComponentType<any>;
  [x: string]: any;
}

type ErrorHandlerState = {
  hasError: boolean;
};

class ErrorHandlerInner extends React.Component<
  ErrorHandlerProps,
  ErrorHandlerState
> {
  errorState: ErrorState;

  constructor(props: ErrorHandlerProps) {
    super(props);
    this.errorState = new ErrorState(this);
    this.state = {
      hasError: false,
    };
  }

  clearReactErrorState = () => {
    this.setState({ hasError: false });
  };

  static getDerivedStateFromError() {
    // We use this flag just to render null while we wait for mobx to update and rerender with the fallback component.
    // If we didn't do this we'd rerender the crashed components and break this boundary for some reason.
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.errorState.SetError(error);
    analytics.logError(error);
  }

  render() {
    if (this.errorState.error) {
      const { ErrorComponent } = this.props;
      return <ErrorComponent {...this.props} errorState={this.errorState} />;
    }
    if (this.state.hasError) {
      return null;
    }
    return (
      <React.Fragment key={this.errorState.key}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export const ErrorHandler = observer(ErrorHandlerInner);
